import React from 'react';
import { motion } from 'framer-motion';
import { ArrowDownToLine, Key, ArrowUpRight, Calculator } from 'lucide-react';
import { useThemeMode } from '../../hooks/useThemeMode';

const CHAINS = [
  '/logos/ethereum.png',
  '/logos/solana.png',
  '/logos/bnb.png',
  '/logos/polygon.png',
  '/logos/base.png',
  '/logos/hyperliquid.png',
];

const TOOLS = [
  { icon: ArrowDownToLine, name: 'deposit',   desc: 'Lock funds into InterNull on any supported chain' },
  { icon: Key,              name: 'list_keys', desc: 'Query ready, pending, or claimed OTS keys' },
  { icon: ArrowUpRight,     name: 'withdraw',  desc: 'Burn a key and release funds to any address' },
  { icon: Calculator,       name: 'quote',     desc: 'Estimate fees, slippage, and delivery chain' },
];

const FEATURES = [
  {
    eyebrow: 'AUTH METHOD',
    title: 'No API Key Required',
    body: 'The MCP server authenticates with the agent’s own wallet signature. Nothing to provision, nothing to rotate.',
    render: (colors) => (
      <svg viewBox="0 0 300 150" preserveAspectRatio="xMidYMid meet" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
        <g fontFamily="'JetBrains Mono', monospace" fontSize="11">
          <rect x="18" y="40" rx="4" width="168" height="26" fill="none" stroke={colors.border} />
          <foreignObject x="24" y="42" width="162" height="22">
            <div xmlns="http://www.w3.org/1999/xhtml" style={{ display: 'flex', alignItems: 'center', gap: 6, color: colors.textLow, fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>
              <span style={{ opacity: 0.7 }}>🔑</span>
              <span style={{ textDecoration: 'line-through' }}>sk_live_A9f2…c73x</span>
            </div>
          </foreignObject>
          <line x1="20" y1="64" x2="184" y2="40" stroke={colors.accent} strokeWidth="1.6" strokeLinecap="round" />
          <rect x="108" y="96" rx="4" width="176" height="30" fill="none" stroke={colors.accent} style={{ filter: `drop-shadow(0 0 6px ${colors.accentGlow})` }} />
          <foreignObject x="114" y="98" width="170" height="26">
            <div xmlns="http://www.w3.org/1999/xhtml" style={{ display: 'flex', alignItems: 'center', gap: 6, color: colors.accent, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, paddingTop: 4 }}>
              <span>✒</span><span>wallet.sign(nonce)</span>
            </div>
          </foreignObject>
        </g>
      </svg>
    ),
  },
  {
    eyebrow: 'KEY STORAGE · LOCAL',
    title: 'Autonomous Custody',
    body: 'Agents hold their own OTS keys locally. They can choose when and where to withdraw without revealing the deposit.',
    render: (colors) => (
      <svg viewBox="0 0 300 150" preserveAspectRatio="xMidYMid meet" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
        <circle cx="150" cy="75" r="48" fill="none" stroke={colors.border} strokeWidth="1" strokeDasharray="3 3" />
        <circle cx="150" cy="75" r="64" fill="none" stroke={colors.border} strokeWidth="1" strokeDasharray="2 6" opacity="0.6" />
        <rect x="132" y="57" width="36" height="36" rx="6" fill={colors.panelBg} stroke={colors.accent} strokeWidth="1.4" />
        <circle cx="143" cy="72" r="2" fill={colors.accent} />
        <circle cx="157" cy="72" r="2" fill={colors.accent} />
        <path d="M 140 83 Q 150 87 160 83" stroke={colors.accent} strokeWidth="1.2" fill="none" />
        {[
          { x: 102, y: 75 },
          { x: 198, y: 75 },
          { x: 150, y: 27 },
          { x: 150, y: 123 },
        ].map((p, i) => (
          <g key={i} transform={`translate(${p.x},${p.y})`}>
            <rect x="-12" y="-7" width="24" height="14" rx="2" fill={colors.panelBg} stroke={colors.accent} strokeWidth="1" />
            <text x="0" y="3" textAnchor="middle" fontFamily="'JetBrains Mono', monospace" fontSize="8" fill={colors.accent}>OTS</text>
          </g>
        ))}
        <circle r="3" fill={colors.accent} filter={`drop-shadow(0 0 5px ${colors.accentGlow})`}>
          <animateMotion dur="7s" repeatCount="indefinite" path="M 150 27 A 48 48 0 1 1 150 123 A 48 48 0 1 1 150 27" />
        </circle>
      </svg>
    ),
  },
  {
    eyebrow: 'UNLINKABILITY PROOF',
    title: 'Deterministic Privacy',
    body: 'Every agent withdrawal is mathematically unlinkable to the deposit. No counterparty can correlate agent activity.',
    render: (colors) => (
      <svg viewBox="0 0 300 150" preserveAspectRatio="xMidYMid meet" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
        <foreignObject x="18" y="34" width="140" height="28">
          <div xmlns="http://www.w3.org/1999/xhtml" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', border: `1px solid ${colors.border}`, borderRadius: 4, color: colors.textLow, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, background: colors.panelBg }}>
            <img src="/logos/ethereum.png" alt="" style={{ width: 12, height: 12, borderRadius: '50%' }} />
            <span>deposit 0x4f3a</span>
          </div>
        </foreignObject>
        <foreignObject x="142" y="94" width="140" height="28">
          <div xmlns="http://www.w3.org/1999/xhtml" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', border: `1px solid ${colors.border}`, borderRadius: 4, color: colors.textLow, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, background: colors.panelBg }}>
            <img src="/logos/solana.png" alt="" style={{ width: 12, height: 12, borderRadius: '50%' }} />
            <span>withdraw 8HtK</span>
          </div>
        </foreignObject>
        <path d="M 70 62 Q 130 62 150 82 T 230 108" stroke={colors.border} strokeWidth="1" fill="none" strokeDasharray="4 4" />
        <g transform="translate(150,82)">
          <circle r="22" fill={colors.panelBg} stroke={colors.accent} strokeWidth="1.4" filter={`drop-shadow(0 0 8px ${colors.accentGlow})`} />
          <text x="0" y="5" textAnchor="middle" fontFamily="'JetBrains Mono', monospace" fontSize="18" fontWeight="700" fill={colors.accent}>≠</text>
        </g>
      </svg>
    ),
  },
];

const AgentSection = () => {
  const { isDark } = useThemeMode();

  const accent = isDark ? '#ffffff' : '#171717';
  const accentGlow = isDark ? 'rgba(255,255,255,0.45)' : 'rgba(23,23,23,0.35)';
  const textHi = isDark ? '#ffffff' : '#171717';
  const textMid = isDark ? '#a3a3a3' : '#525252';
  const textLow = isDark ? '#525252' : '#a3a3a3';
  const dim = isDark ? '#525252' : '#a3a3a3';
  const cardBg = isDark ? '#0a0a0a' : '#fafafa';
  const cardBord = isDark ? '#171717' : '#e5e5e5';
  const panelBg = isDark ? '#0f0f0f' : '#f5f5f5';
  const panelBg3 = isDark ? '#141414' : '#ffffff';
  const border = isDark ? '#262626' : '#e5e5e5';

  const colors = { accent, accentGlow, textHi, textMid, textLow, border, cardBord, cardBg, panelBg };

  return (
    <section
      id="agents"
      className="relative py-24 px-6"
      style={{ borderTop: `1px solid ${cardBord}`, background: isDark ? '#050505' : '#fafafa' }}
    >
      <style>{`
        @keyframes intnl-ag-march { to { stroke-dashoffset: -520; } }
        @keyframes intnl-ag-msg {
          0%, 6%   { opacity: 0; }
          12%, 36% { opacity: 1; }
          46%, 100%{ opacity: 0; }
        }
        @keyframes intnl-ag-tp {
          0%   { offset-distance: 0%;   opacity: 0; }
          10%  { opacity: 1; }
          90%  { opacity: 1; }
          100% { offset-distance: 100%; opacity: 0; }
        }
        .intnl-ag-lane {
          fill: none;
          stroke: ${accent};
          stroke-width: 1;
          stroke-dasharray: 120 400;
          opacity: 0.9;
          animation: intnl-ag-march 2.6s linear infinite;
        }
        .intnl-ag-lane.acid { filter: drop-shadow(0 0 3px ${accentGlow}); }
        .intnl-ag-msg {
          font-family: 'JetBrains Mono', monospace;
          font-size: 10px;
          fill: ${accent};
          letter-spacing: 0.08em;
          opacity: 0;
          animation: intnl-ag-msg 6s linear infinite;
        }
        .intnl-ag-msg.m2 { animation-delay: 1.5s; }
        .intnl-ag-msg.m3 { animation-delay: 3s; }
        .intnl-ag-msg.m4 { animation-delay: 4.5s; }
        .intnl-ag-particle { animation: intnl-ag-tp 1.8s linear infinite; }
      `}</style>

      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-10"
        >
          <div className="font-mono text-xs tracking-[0.22em] uppercase mb-4" style={{ color: textLow }}>
            Built for Agents · MCP Server Live
          </div>
          <h2 className="text-4xl md:text-5xl font-bold leading-tight mb-4" style={{ color: textHi }}>
            AI agents, <span style={{ color: textMid }}>with a wallet of their own.</span>
          </h2>
          <p className="text-lg leading-relaxed max-w-3xl" style={{ color: textMid }}>
            InterNull ships a Model Context Protocol server with 18 tools so any LLM (Claude, GPT, local) can deposit,
            custody keys, and withdraw with full privacy. No API key. Bring your own wallet or spin up a burner.
          </p>
        </motion.div>

        {/* Agent interaction diagram */}
        <div
          className="p-8 rounded-lg relative overflow-hidden"
          style={{
            background: cardBg,
            border: `1px solid ${cardBord}`,
            boxShadow: isDark ? '0 10px 40px rgba(0,0,0,0.4)' : '0 10px 30px rgba(0,0,0,0.06)',
          }}
        >
          <div className="font-mono text-xs tracking-[0.22em] uppercase mb-5" style={{ color: textLow }}>
            Agent ⇄ InterNull · Interaction Loop
          </div>

          <div className="relative" style={{ height: 340 }}>
            <svg viewBox="0 0 1200 340" preserveAspectRatio="xMidYMid meet" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible' }}>
              {/* Lanes */}
              <path className="intnl-ag-lane" d="M 220 100 L 560 100" />
              <path className="intnl-ag-lane acid" d="M 560 140 L 800 140" />
              <path className="intnl-ag-lane acid" d="M 800 200 L 560 200" />
              <path className="intnl-ag-lane" d="M 560 240 L 220 240" />

              {/* Agent node */}
              <g transform="translate(60,120)">
                <rect width="160" height="100" rx="4" fill={panelBg} stroke={cardBord} />
                <rect x="20" y="26" width="32" height="32" rx="16" fill="none" stroke={accent} />
                <text x="36" y="47" textAnchor="middle" fontFamily="'JetBrains Mono', monospace" fontSize="13" fill={accent} fontWeight="700">AI</text>
                <text x="64" y="38" fontFamily="Inter, sans-serif" fontSize="13" fontWeight="700" fill={textHi}>Agent</text>
                <text x="64" y="56" fontFamily="'JetBrains Mono', monospace" fontSize="10" letterSpacing="0.15em" fill={textLow}>CLAUDE · GPT · LOCAL</text>
                <text x="64" y="74" fontFamily="'JetBrains Mono', monospace" fontSize="9" letterSpacing="0.1em" fill={dim}>BYO WALLET</text>
              </g>

              {/* MCP server */}
              <g transform="translate(480,80)">
                <rect width="240" height="180" rx="4" fill={panelBg3} stroke={accent} style={{ filter: `drop-shadow(0 0 10px ${accentGlow})` }} />
                <text x="20" y="34" fontFamily="Inter, sans-serif" fontSize="13" fontWeight="700" fill={textHi}>InterNull MCP</text>
                <text x="20" y="52" fontFamily="'JetBrains Mono', monospace" fontSize="10" letterSpacing="0.15em" fill={textLow}>18 TOOLS · STDIO / HTTP</text>
                <g fontFamily="'JetBrains Mono', monospace" fontSize="10" fill={textMid}>
                  <rect x="20" y="68"  width="200" height="22" rx="2" fill={panelBg} stroke={cardBord} />
                  <text x="28" y="83">→ deposit(chain, token, amount)</text>
                  <rect x="20" y="94"  width="200" height="22" rx="2" fill={panelBg} stroke={cardBord} />
                  <text x="28" y="109">→ list_keys(status?)</text>
                  <rect x="20" y="120" width="200" height="22" rx="2" fill={panelBg} stroke={cardBord} />
                  <text x="28" y="135" fill={accent}>→ withdraw(key, chain, to)</text>
                  <rect x="20" y="146" width="200" height="22" rx="2" fill={panelBg} stroke={cardBord} />
                  <text x="28" y="161">→ quote(from, to, amount)</text>
                </g>
              </g>

              {/* Chain grid */}
              <g transform="translate(800,95)">
                <rect width="200" height="150" rx="4" fill={panelBg} stroke={cardBord} />
                <text x="20" y="32" fontFamily="Inter, sans-serif" fontSize="13" fontWeight="700" fill={textHi}>Chain</text>
                <text x="20" y="50" fontFamily="'JetBrains Mono', monospace" fontSize="10" letterSpacing="0.15em" fill={textLow}>6 NETWORKS · ON-CHAIN TX</text>
                <foreignObject x="16" y="62" width="168" height="76">
                  <div xmlns="http://www.w3.org/1999/xhtml" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 6, padding: 4 }}>
                    {CHAINS.map((src, i) => (
                      <div
                        key={i}
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          overflow: 'hidden',
                          background: panelBg3,
                          border: `1px solid ${border}`,
                          margin: '0 auto',
                        }}
                      >
                        <img src={src} alt="" style={{ width: '100%', height: '100%', display: 'block' }} />
                      </div>
                    ))}
                  </div>
                </foreignObject>
              </g>

              {/* Messages */}
              <text className="intnl-ag-msg"    x="290" y="92">&gt; deposit(ETH, 100 USDC)</text>
              <text className="intnl-ag-msg m2" x="630" y="132">&gt; tx signed → chain</text>
              <text className="intnl-ag-msg m3" x="720" y="192" style={{ textAnchor: 'end' }}>&lt; keys: 7 OTS ready</text>
              <text className="intnl-ag-msg m4" x="470" y="232" style={{ textAnchor: 'end' }}>&lt; withdraw(sol, 10, 8HtK…)</text>

              {/* Traveling particles */}
              {[
                { d: 'M 220 100 L 560 100', delay: 0 },
                { d: 'M 560 140 L 800 140', delay: 0.4 },
                { d: 'M 800 200 L 560 200', delay: 0.9 },
                { d: 'M 560 240 L 220 240', delay: 1.3 },
              ].map((p, i) => (
                <circle
                  key={i}
                  r="5"
                  fill={accent}
                  filter={`drop-shadow(0 0 6px ${accentGlow})`}
                  className="intnl-ag-particle"
                  style={{
                    offsetPath: `path('${p.d}')`,
                    offsetRotate: '0deg',
                    animationDelay: `${p.delay}s`,
                  }}
                />
              ))}
            </svg>
          </div>

          {/* Tools row */}
          <div
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-7 pt-6"
            style={{ borderTop: `1px solid ${cardBord}` }}
          >
            {TOOLS.map(({ icon: Icon, name, desc }) => (
              <div key={name} className="font-mono text-[11px] flex gap-2.5 items-start" style={{ color: textMid }}>
                <Icon size={14} style={{ color: textLow, flexShrink: 0, marginTop: 2 }} />
                <span>
                  <b style={{ color: textHi, fontWeight: 500, display: 'block', marginBottom: 2, letterSpacing: 0 }}>{name}</b>
                  {desc}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Feature cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-9">
          {FEATURES.map((f, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="rounded-lg overflow-hidden flex flex-col transition-all"
              style={{
                background: cardBg,
                border: `1px solid ${cardBord}`,
              }}
            >
              <div
                className="relative"
                style={{
                  height: 150,
                  background: isDark
                    ? 'linear-gradient(135deg, rgba(255,255,255,0.02), rgba(255,255,255,0.04))'
                    : 'linear-gradient(135deg, rgba(0,0,0,0.02), rgba(0,0,0,0.04))',
                  borderBottom: `1px solid ${cardBord}`,
                }}
              >
                <div
                  className="absolute top-3 left-3 font-mono flex items-center gap-2"
                  style={{ fontSize: 9, letterSpacing: '0.2em', color: textLow, zIndex: 2 }}
                >
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      background: accent,
                      boxShadow: `0 0 4px ${accentGlow}`,
                      display: 'inline-block',
                    }}
                  />
                  {f.eyebrow}
                </div>
                {f.render(colors)}
              </div>
              <div className="p-5">
                <h4 className="text-base font-semibold mb-2" style={{ color: textHi }}>
                  {f.title}
                </h4>
                <p className="text-sm leading-relaxed" style={{ color: textMid }}>
                  {f.body}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AgentSection;
